import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, FileWarning, LifeBuoy, Flag, Scale, ScrollText, Plus, Trash2, Clock, Store, ShieldCheck, BadgeCheck, Ban, Sparkles } from "lucide-react";
import Seo from "@/components/Seo";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

const MOD_STATUSES = ["complaint_filed", "response_pending", "under_discussion", "resolved_by_agreement", "unresolved", "no_response", "under_moderation", "closed"];
const STAFF_ROLES = ["moderator", "resolution_coordinator", "admin", "super_admin"];
const ROLE_LABEL = { super_admin: "Super Admin", admin: "Admin", moderator: "Moderator", resolution_coordinator: "Resolution Coord." };

export default function Admin() {
  const [me, setMe] = useState(null);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [reports, setReports] = useState([]);
  const [logs, setLogs] = useState([]);
  const [cats, setCats] = useState([]);
  const [newCat, setNewCat] = useState({ type: "customer", name: "" });

  const perms = me?.permissions || [];
  const can = (a) => perms.includes("*") || perms.includes(a);

  const load = (p = me) => {
    const ok = (a) => (p?.permissions || []).includes("*") || (p?.permissions || []).includes(a);
    if (ok("stats:view")) api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    if (ok("complaints:view")) api.get("/admin/complaints").then((r) => setComplaints(r.data)).catch(() => {});
    if (ok("users:view")) api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    if (ok("vendors:view")) api.get("/admin/vendors").then((r) => setVendors(r.data)).catch(() => {});
    if (ok("tickets:view")) api.get("/admin/tickets").then((r) => setTickets(r.data)).catch(() => {});
    if (ok("reports:view")) api.get("/admin/reports").then((r) => setReports(r.data)).catch(() => {});
    if (ok("audit:view")) api.get("/admin/audit-logs").then((r) => setLogs(r.data)).catch(() => {});
    if (ok("categories:manage")) api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
  };

  useEffect(() => {
    api.get("/admin/me").then((r) => { setMe(r.data); load(r.data); }).catch(() => toast.error("Staff access required"));
  }, []);

  const moderate = async (cid, status) => {
    try { await api.post(`/admin/complaints/${cid}/moderate`, { status, reason: "Admin moderation action" }); toast.success("Updated"); load(); }
    catch { toast.error("Failed"); }
  };
  const approve = async (cid) => {
    try { await api.post(`/admin/complaints/${cid}/approve`); toast.success("Complaint approved & made visible"); load(); }
    catch { toast.error("Failed"); }
  };
  const removeComplaint = async (cid) => {
    if (!window.confirm("Remove this complaint permanently? This is logged in the audit trail.")) return;
    try { await api.delete(`/admin/complaints/${cid}`); toast.success("Complaint removed"); load(); }
    catch { toast.error("Failed"); }
  };
  const reject = async (cid) => {
    try { await api.post(`/admin/complaints/${cid}/moderate`, { status: "under_moderation", reason: "Rejected during approval review" }); toast.success("Complaint rejected & kept hidden"); load(); }
    catch { toast.error("Failed"); }
  };
  const resolveTicket = async (tid) => {
    try { await api.post(`/admin/tickets/${tid}/resolve`); toast.success("Ticket resolved"); load(); }
    catch { toast.error("Failed"); }
  };
  const addCat = async () => {
    if (!newCat.name) return;
    try { await api.post("/categories", newCat); toast.success("Category added"); setNewCat({ ...newCat, name: "" }); load(); }
    catch { toast.error("Failed"); }
  };
  const delCat = async (cid) => { try { await api.delete(`/categories/${cid}`); load(); } catch { toast.error("Failed"); } };

  const toggleRole = async (u, role) => {
    const has = (u.roles || []).includes(role);
    const base = (u.roles || []).filter((r) => ["customer", "vendor", "both"].includes(r));
    const staff = (u.roles || []).filter((r) => STAFF_ROLES.includes(r));
    const nextStaff = has ? staff.filter((r) => r !== role) : [...staff, role];
    const roles = [...(base.length ? base : [u.user_type || "customer"]), ...nextStaff];
    try { await api.post(`/admin/users/${u.id}/roles`, { roles }); toast.success("Roles updated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to update roles"); }
  };
  const toggleSuspend = async (u) => {
    try { await api.post(`/admin/users/${u.id}/suspend`, { suspended: !u.suspended }); toast.success(u.suspended ? "Account reactivated" : "Account suspended"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const setVendorVerify = async (v, val) => {
    try { await api.post(`/admin/vendors/${v.id}/${val ? "verify" : "unverify"}`); toast.success("Vendor updated"); load(); }
    catch { toast.error("Failed"); }
  };
  const toggleFeature = async (v) => {
    try { await api.post(`/admin/vendors/${v.id}/feature`); toast.success("Vendor updated"); load(); }
    catch { toast.error("Failed"); }
  };

  const STAT_CARDS = stats ? [
    ["Users", stats.users, Users], ["Complaints", stats.complaints, FileWarning],
    ["Pending approval", stats.pending_approval, Clock], ["Open", stats.open_complaints, Scale],
    ["Resolved", stats.resolved, Scale], ["Under moderation", stats.under_moderation, Flag],
    ["Vendors", stats.vendors, Store], ["Open reports", stats.reports, Flag],
  ] : [];

  const pending = complaints.filter((c) => !c.public_visible && !["under_moderation", "closed", "withdrawn"].includes(c.status));

  const TABS = [
    can("complaints:view") && { v: "pending", label: `Pending approval${pending.length ? ` (${pending.length})` : ""}` },
    can("complaints:view") && { v: "complaints", label: "Complaints" },
    can("users:view") && { v: "users", label: "Users" },
    can("vendors:view") && { v: "vendors", label: "Vendors" },
    can("categories:manage") && { v: "categories", label: "Categories" },
    can("tickets:view") && { v: "tickets", label: "Tickets" },
    can("reports:view") && { v: "reports", label: "Reports" },
    can("audit:view") && { v: "audit", label: "Audit Logs" },
  ].filter(Boolean);

  if (!me) return <div className="ca-container py-24 text-slate-500">Loading admin…</div>;

  return (
    <div className="ca-container py-10">
      <Seo title="Admin Dashboard | ComplaintAdvisor" noindex />
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900 flex items-center gap-2"><ScrollText className="h-8 w-8 text-teal" /> Master Admin</h1>
          <div className="flex flex-wrap items-center gap-1.5 mt-2" data-testid="admin-role-badges">
            {(me.roles || []).filter((r) => STAFF_ROLES.includes(r)).map((r) => (
              <span key={r} className="inline-flex items-center gap-1 rounded-full bg-teal-brand/10 text-teal px-2.5 py-0.5 text-xs font-semibold">
                <ShieldCheck className="h-3.5 w-3.5" /> {ROLE_LABEL[r] || r}
              </span>
            ))}
            <span className="text-xs text-slate-400 ml-1">{me.email}</span>
          </div>
        </div>
        {can("feeds:manage") && (
          <a href="/admin/discovery"><Button variant="outline" data-testid="goto-discovery-admin"><Store className="h-4 w-4 mr-1" /> Discovery / Feeds</Button></a>
        )}
      </div>

      {can("stats:view") && (
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {STAT_CARDS.map(([l, v, Icon]) => (
            <div key={l} className="rounded-2xl border border-slate-200 bg-white p-4">
              <Icon className="h-5 w-5 text-teal" /><p className="text-2xl font-display font-bold mt-2">{v}</p><p className="text-xs text-slate-500">{l}</p>
            </div>
          ))}
        </div>
      )}

      <div className="mt-6">
        <Tabs defaultValue={TABS[0]?.v}>
          <TabsList className="flex-wrap h-auto">
            {TABS.map((t) => <TabsTrigger key={t.v} value={t.v} data-testid={`admin-tab-${t.v}`}>{t.label}</TabsTrigger>)}
          </TabsList>

          {can("complaints:view") && (
          <TabsContent value="pending" className="mt-4 space-y-2" data-testid="pending-approval-list">
            <p className="text-sm text-slate-500">Complaints awaiting review before they become publicly visible.</p>
            {pending.map((c) => (
              <div key={c.id} className="rounded-xl border border-amber-200 bg-amber-50/40 p-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-mono text-xs text-slate-400">{c.complaint_code}</p>
                  <p className="text-sm font-medium text-slate-800">{c.party?.name} · {c.party?.category}</p>
                  <p className="text-xs text-slate-500 mt-0.5 max-w-xl line-clamp-2">{c.description}</p>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={c.status} />
                  {can("complaints:approve") && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => approve(c.id)} data-testid={`pending-approve-${c.id}`}>Approve</Button>}
                  {can("complaints:moderate") && <Button size="sm" variant="outline" className="text-amber-700 border-amber-300 hover:bg-amber-100" onClick={() => reject(c.id)} data-testid={`pending-reject-${c.id}`}>Reject</Button>}
                  {can("complaints:delete") && <Button size="sm" variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => removeComplaint(c.id)} data-testid={`pending-remove-${c.id}`}><Trash2 className="h-4 w-4" /></Button>}
                </div>
              </div>
            ))}
            {!pending.length && <p className="text-sm text-slate-400 py-4">No complaints pending approval.</p>}
          </TabsContent>
          )}

          {can("complaints:view") && (
          <TabsContent value="complaints" className="mt-4 space-y-2">
            {complaints.map((c) => (
              <div key={c.id} className="rounded-xl border border-slate-200 bg-white p-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-mono text-xs text-slate-400">{c.complaint_code}</p>
                  <p className="text-sm font-medium text-slate-800">{c.party?.name} · {c.party?.category}</p>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={c.status} />
                  {!c.public_visible && <span className="text-[10px] font-bold uppercase text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">Hidden</span>}
                  {can("complaints:moderate") ? (
                    <Select value={c.status} onValueChange={(v) => moderate(c.id, v)}>
                      <SelectTrigger className="w-40" data-testid={`moderate-${c.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{MOD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : null}
                  {can("complaints:approve") && !c.public_visible && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => approve(c.id)} data-testid={`approve-${c.id}`}>Approve</Button>}
                  {can("complaints:delete") && <Button size="sm" variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => removeComplaint(c.id)} data-testid={`remove-${c.id}`}><Trash2 className="h-4 w-4" /></Button>}
                </div>
              </div>
            ))}
          </TabsContent>
          )}

          {can("users:view") && (
          <TabsContent value="users" className="mt-4">
            <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr><th className="text-left p-3">Name</th><th className="text-left p-3">Email</th><th className="text-left p-3">Type</th><th className="text-left p-3">Roles / access</th><th className="text-left p-3">Status</th></tr></thead>
                <tbody>{users.map((u) => (
                  <tr key={u.id} className="border-t border-slate-100 align-top" data-testid={`user-row-${u.id}`}>
                    <td className="p-3">{u.name}</td>
                    <td className="p-3 font-mono text-xs">{u.email}</td>
                    <td className="p-3">{u.user_type}</td>
                    <td className="p-3">
                      {can("users:manage") ? (
                        <div className="flex flex-wrap gap-1.5">
                          {STAFF_ROLES.map((r) => {
                            const active = (u.roles || []).includes(r);
                            return (
                              <button key={r} onClick={() => toggleRole(u, r)} data-testid={`role-${r}-${u.id}`}
                                className={`rounded-full px-2 py-0.5 text-[11px] font-medium border transition-colors ${active ? "bg-teal-brand text-white border-teal-brand" : "bg-white text-slate-500 border-slate-200 hover:border-teal-brand/50"}`}>
                                {ROLE_LABEL[r]}
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-500">{(u.roles || []).join(", ")}</span>
                      )}
                    </td>
                    <td className="p-3">
                      {can("users:manage") ? (
                        <Button size="sm" variant="outline" onClick={() => toggleSuspend(u)} data-testid={`suspend-${u.id}`}
                          className={u.suspended ? "text-emerald-700 border-emerald-300" : "text-rose-600 border-rose-200"}>
                          <Ban className="h-3.5 w-3.5 mr-1" /> {u.suspended ? "Reactivate" : "Suspend"}
                        </Button>
                      ) : (
                        <span className={`text-xs ${u.suspended ? "text-rose-600" : "text-emerald-600"}`}>{u.suspended ? "Suspended" : "Active"}</span>
                      )}
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
            {!can("users:manage") && <p className="text-xs text-slate-400 mt-2">Only a Super Admin can change roles or suspend accounts.</p>}
          </TabsContent>
          )}

          {can("vendors:view") && (
          <TabsContent value="vendors" className="mt-4 space-y-2" data-testid="vendors-list">
            {vendors.map((v) => (
              <div key={v.id} className="rounded-xl border border-slate-200 bg-white p-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                    {v.name}
                    {v.verified && <span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 text-[10px]"><BadgeCheck className="h-3 w-3" /> Verified</span>}
                    {v.sponsored && <span className="inline-flex items-center gap-1 text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 text-[10px]"><Sparkles className="h-3 w-3" /> Featured</span>}
                  </p>
                  <p className="text-xs text-slate-500">{v.category} · {v.city} · {v.response_rate}% response · {v.resolution_count} resolved</p>
                </div>
                {can("vendors:verify") && (
                  <div className="flex items-center gap-2">
                    {v.verified
                      ? <Button size="sm" variant="outline" onClick={() => setVendorVerify(v, false)} data-testid={`vendor-unverify-${v.id}`}>Unverify</Button>
                      : <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => setVendorVerify(v, true)} data-testid={`vendor-verify-${v.id}`}>Verify</Button>}
                    <Button size="sm" variant="outline" onClick={() => toggleFeature(v)} data-testid={`vendor-feature-${v.id}`}>{v.sponsored ? "Unfeature" : "Feature"}</Button>
                  </div>
                )}
              </div>
            ))}
            {!vendors.length && <p className="text-sm text-slate-500">No vendors yet.</p>}
          </TabsContent>
          )}

          {can("categories:manage") && (
          <TabsContent value="categories" className="mt-4">
            <div className="flex flex-wrap gap-2 items-end mb-4 rounded-xl border border-slate-200 bg-white p-4">
              <div><label className="text-xs text-slate-500">Type</label>
                <Select value={newCat.type} onValueChange={(v) => setNewCat({ ...newCat, type: v })}>
                  <SelectTrigger className="w-32" data-testid="newcat-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="customer">Customer</SelectItem><SelectItem value="vendor">Vendor</SelectItem></SelectContent>
                </Select>
              </div>
              <Input placeholder="Category name" value={newCat.name} onChange={(e) => setNewCat({ ...newCat, name: e.target.value })} className="w-64" data-testid="newcat-name" />
              <Button onClick={addCat} className="bg-teal-brand text-white" data-testid="add-category-btn"><Plus className="h-4 w-4 mr-1" /> Add</Button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {cats.map((c) => (
                <div key={c.id} className="rounded-xl border border-slate-200 bg-white p-3 flex items-center justify-between">
                  <span className="text-sm text-slate-700">{c.name} <span className="text-xs text-slate-400">({c.type})</span></span>
                  <Button variant="ghost" size="icon" onClick={() => delCat(c.id)} data-testid={`del-cat-${c.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                </div>
              ))}
            </div>
          </TabsContent>
          )}

          {can("tickets:view") && (
          <TabsContent value="tickets" className="mt-4 space-y-2">
            {tickets.map((t) => (
              <div key={t.id} className="rounded-xl border border-slate-200 bg-white p-4 flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold">{t.ticket_code} · {t.issue_type} <span className="text-xs font-normal text-slate-400">({t.status})</span></p>
                  <p className="text-xs text-slate-500 mt-1">{t.description}</p>
                </div>
                {t.status !== "resolved" && can("tickets:resolve") && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => resolveTicket(t.id)} data-testid={`resolve-ticket-${t.id}`}>Resolve</Button>}
              </div>
            ))}
            {!tickets.length && <p className="text-sm text-slate-500">No tickets.</p>}
          </TabsContent>
          )}

          {can("reports:view") && (
          <TabsContent value="reports" className="mt-4 space-y-2">
            {reports.map((r) => (
              <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4">
                <p className="text-sm font-semibold">{r.reason}</p>
                <p className="text-xs text-slate-500">Complaint: {r.complaint_id || "—"} · {r.status}</p>
              </div>
            ))}
            {!reports.length && <p className="text-sm text-slate-500">No reports.</p>}
          </TabsContent>
          )}

          {can("audit:view") && (
          <TabsContent value="audit" className="mt-4 space-y-1">
            {logs.map((l) => (
              <div key={l.id} className="rounded-lg border border-slate-100 bg-white p-3 text-xs font-mono text-slate-600">
                [{l.created_at?.slice(0, 19)}] {l.action} · {l.affected_record} · {l.previous_status || "—"} → {l.new_status || "—"} · {l.reason}
              </div>
            ))}
            {!logs.length && <p className="text-sm text-slate-500">No audit logs yet.</p>}
          </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
}
